import React, { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Tooltip } from 'antd';

const schemaCache = new Map();

const operationToModel = (operationType) => {
  switch (operationType) {
    case 'createPO': return 'purchase.order';
    case 'createQuotation':
    case 'createSO':
    case 'confirmSO': return 'sale.order';
    case 'createInvoice':
    case 'postInvoice':
    case 'createCreditNote': return 'account.move';
    case 'createDeliveryNote':
    case 'createGoodsReceive': return 'stock.picking';
    case 'createReturn': return 'stock.picking';
    case 'registerPayment': return 'account.payment';
    case 'lookupPartner': return 'res.partner';
    case 'lookupProduct': return 'product.product';
    case 'lookupStock': return 'stock.quant';
    default: return null;
  }
};

const buildOptions = (fields, prefix) => {
  if (!fields) return [];
  const entries = Object.entries(fields);
  return entries.map(([fieldName, meta]) => {
    const label = meta && typeof meta === 'object' && meta.string ? `${fieldName} — ${meta.string}` : fieldName;
    const path = prefix === 'lines' ? `lines[0].${fieldName}` : `${prefix}.${fieldName}`;
    return { value: path, label };
  });
};

const sectionsForOperation = (operationType) => {
  const base = [
    { prefix: 'partner', label: 'partner' },
    { prefix: 'lines', label: 'lines[0]' },
    { prefix: 'terms', label: 'terms' },
    { prefix: 'metadata', label: 'metadata' },
    { prefix: 'source', label: 'source' }
  ];
  if (operationType === 'lookupPartner' || operationType === 'lookupProduct' || operationType === 'lookupStock') {
    return [{ prefix: '', label: 'query/domain' }];
  }
  return base;
};

/**
 * AutoComplete for Odoo field paths driven by `/api/odoo/{providerKey}/schema/{model}`.
 * Falls back to plain free-text input when provider key or operation are missing.
 */
const OdooFieldAutoComplete = ({ providerKey, operationType, value, onChange, placeholder }) => {
  const model = operationToModel(operationType);
  const [fields, setFields] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!providerKey || !model) {
      setFields(null);
      return;
    }
    const cacheKey = `${providerKey}::${model}`;
    if (schemaCache.has(cacheKey)) {
      setFields(schemaCache.get(cacheKey));
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/api/odoo/${encodeURIComponent(providerKey)}/schema/${encodeURIComponent(model)}`, {
      credentials: 'include'
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`schema ${r.status}`)))
      .then(json => {
        if (cancelled) return;
        const f = (json && json.fields) || null;
        schemaCache.set(cacheKey, f);
        setFields(f);
      })
      .catch(() => {
        if (cancelled) return;
        schemaCache.set(cacheKey, null);
        setFields(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [providerKey, model]);

  const options = useMemo(() => {
    if (!fields) return [];
    return sectionsForOperation(operationType).flatMap(section => {
      const opts = buildOptions(fields, section.prefix || section.label);
      return opts.length > 0
        ? [{ label: section.label, options: opts }]
        : [];
    });
  }, [fields, operationType]);

  const filterOption = (input, option) => {
    if (!input) return true;
    const v = (option && option.value) || '';
    const l = (option && option.label) || '';
    return v.toLowerCase().includes(input.toLowerCase()) || l.toLowerCase().includes(input.toLowerCase());
  };

  const tooltip = !providerKey
    ? 'Set Odoo provider key (Data tab) to enable schema-based suggestions.'
    : !fields && !loading
      ? 'Schema not yet loaded; you can still type free text (partner.name, lines[0].qty …).'
      : null;

  const ac = (
    <AutoComplete
      value={value || ''}
      onChange={(v) => onChange(v)}
      options={options}
      placeholder={placeholder || 'partner.name | lines[0].product.code | lines[0].qty'}
      filterOption={filterOption}
      style={{ width: '100%' }}
      allowClear
    />
  );
  return tooltip ? <Tooltip title={tooltip}>{ac}</Tooltip> : ac;
};

export default OdooFieldAutoComplete;
